// --- React and Redux
import React from 'react';
import {useSelector} from 'react-redux';
// --- UI Components
import {Col, Row} from 'antd';
import Typography from '@dtci/spellbook/Typography';
import Button from '@dtci/spellbook/Button';
import {ProductsGrid} from './components/grid';
import {ProductsFilterForm} from './components/filter-form';
import {Pagination} from '../../components/Pagination';
import {TwoRowsLayout} from './layout/two-row';
import ToastNotificationProvider from '../../components/ToastNotifications';
// --- Utilities and APIs
import {datastageApi} from '../../api';
import {useProductsActions} from '../../store/products/useProducts';
import {useLoading} from '../../utils/hooks/useLoading';
import {getItemRange} from '../../utils/pagination';
import {filterCriteria} from '../../utils/filterCriteria';
import {exportToCsv} from "../../utils/exportToCsv";
import {tracker} from '../../analytics/snowplow';
import { getCurrentUser } from '../../utils/localStorageService';
/**
 * ProductsPage Component
 *
 * This component renders the Products page
 *
 * @returns {Element}
 * @constructor
 */
export const ProductsPage = () => {
    React.useEffect(() => {
        var currentUser = getCurrentUser();        
        tracker.setUserId(currentUser);
        tracker.trackPageView({ title: 'Products' });
    }, []);
    const {setLoadingTrue, setLoadingFalse} = useLoading();
    const {updateProducts} = useProductsActions();
    const {totalPages, currentPage, filterForm, productRange, productSort, itemsPerPage} =
        useSelector((state) => state.products);

    const handleChange = async (pageNumber) => {
        if (pageNumber === currentPage) return;
        setLoadingTrue();
        try {
            const nextPageRange = getItemRange(pageNumber, itemsPerPage);
            const data = await datastageApi.products(
                filterCriteria(filterForm, '', 'products'),
                nextPageRange,
                productSort,
            );
            updateProducts({
                products: data?.content,
                totalPages: data?.totalPages,
                currentPage: data?.pageable.pageNumber,
                productRange: nextPageRange,
                totalItems: data?.totalElements,
            });
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setLoadingFalse();
        }
    };

    const handleRefresh = async () => {
        setLoadingTrue();
        try {
            const data = await datastageApi.products(
                filterCriteria(filterForm, '', 'products'),
                productRange,
                productSort,
            );
            updateProducts({
                products: data?.content,
                totalItems: data?.totalElements,
                totalPages: data?.totalPages,
                currentPage: data?.pageable.pageNumber,
            });
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setLoadingFalse();
        }
    };

    // Export products to a CSV file
    const handleExport = async () => {
        try {
            setLoadingTrue();

            const data = await datastageApi.products(
                filterCriteria(filterForm, '', 'products'),
                '0,999',
                productSort,
            );

            exportToCsv(data?.content, 'products');
        } catch (error) {
            console.error('Error exporting products:', error);
        } finally {
            setLoadingFalse();
        }
    };

    return (
        <>
            <Row style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20}}>
                <Col>
                    <Typography variant="T50" style={{marginLeft: 20}}>
                        Products
                    </Typography>
                </Col>
                <Col style={{display: 'flex', gap: 8}}>
                    <Button
                        iconType="fa-solid fa-arrow-down-to-line"
                        labelText="Export"
                        styleType="link"
                        onClick={handleExport}
                    >
                        Export
                    </Button>
                    <Button
                        iconType="fa-solid fa-rotate"
                        labelText="Refresh"
                        styleType="link"
                        onClick={handleRefresh}
                    >
                        Refresh
                    </Button>
                </Col>
            </Row>
            <TwoRowsLayout>
                <main data-position="top">
                    <ProductsFilterForm/>
                </main>
                <main data-position="bottom">
                    <ProductsGrid/>
                </main>
            </TwoRowsLayout>
            <Pagination
                pageCount={totalPages}
                onPageChange={(e) => handleChange(e.selected)}
                previousLabel="←"
                nextLabel="→"
                forcePage={currentPage}
            />
            <ToastNotificationProvider/>
        </>
    );
};
